import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  HairlineCard,
  Icons,
  iconSize,
  MenuItem,
  palette,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import type {
  AnalyticsOverview,
  AnalyticsRange,
  BenchmarkMetrics,
  PeerBenchmark,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { useOptionalScope } from '../providers/ScopeProvider';
import {
  daysAgo,
  downloadAnalyticsCsv,
  formatNumber,
  formatRate,
  useAnalyticsAvailability,
  useAnalyticsOverview,
  useContractorBenchmark,
  usePeerBenchmark,
} from '../lib/analytics-api';
import { apiErrorMessage, apiErrorStatus } from '../lib/mutation-toast';
import { stageLabelKey } from '../lib/file-display';
import { frenchFirstOfMonth } from '@munifocus/utils';

const PRESETS = [30, 90, 365] as const;

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <HairlineCard sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" sx={{ color: palette.text.secondary }}>
        {label}
      </Typography>
      <Typography variant="h5" component="p" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
          {hint}
        </Typography>
      )}
    </HairlineCard>
  );
}

function RangePicker({
  range,
  onChange,
}: {
  range: AnalyticsRange;
  onChange: (range: AnalyticsRange) => void;
}) {
  const { t } = useTranslation();
  const preset = PRESETS.find((d) => range.from === daysAgo(d - 1) && range.to === daysAgo(0));
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{ alignItems: { md: 'center' } }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={preset ?? null}
        onChange={(_e, days: number | null) =>
          days && onChange({ from: daysAgo(days - 1), to: daysAgo(0) })
        }
        aria-label={t('gov.analytics.range.presets')}
      >
        {PRESETS.map((d) => (
          <ToggleButton key={d} value={d}>
            {t('gov.analytics.range.lastDays', { count: d })}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          type="date"
          label={t('gov.analytics.range.from')}
          value={range.from}
          onChange={(e) => e.target.value && onChange({ ...range, from: e.target.value })}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: range.to } }}
        />
        <TextField
          size="small"
          type="date"
          label={t('gov.analytics.range.to')}
          value={range.to}
          onChange={(e) => e.target.value && onChange({ ...range, to: e.target.value })}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: range.from } }}
        />
      </Stack>
    </Stack>
  );
}

function OverviewView({ overview }: { overview: AnalyticsOverview }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const o = overview;
  return (
    <Stack spacing={2}>
      {o.tier === 'public' && <Alert severity="info">{t('gov.analytics.publicTier')}</Alert>}
      <Grid container spacing={2}>
        {[
          [t('gov.analytics.kpi.filesOpened'), formatNumber(o.filesOpened, lang)],
          [t('gov.analytics.kpi.filesClosed'), formatNumber(o.filesClosed, lang)],
          [t('gov.analytics.kpi.openAtEnd'), formatNumber(o.openAtEnd, lang)],
          [t('gov.analytics.kpi.noticesServed'), formatNumber(o.noticesServed, lang)],
          [t('gov.analytics.kpi.medianDaysToClose'), formatNumber(o.medianDaysToClose, lang)],
          [
            t('gov.analytics.kpi.deadlinesMet'),
            formatRate(o.deadlines.rate, lang),
            t('gov.analytics.kpi.deadlinesDetail', {
              met: o.deadlines.met,
              missed: o.deadlines.missed,
              pending: o.deadlines.pending,
            }),
          ],
          ...(o.compliance
            ? [
                [
                  t('gov.analytics.kpi.complianceRate'),
                  formatRate(o.compliance.rate, lang),
                  t('gov.analytics.kpi.complianceDetail', {
                    complied: o.compliance.complied,
                    enforced: o.compliance.enforced,
                  }),
                ],
              ]
            : []),
          ...(o.assistantCalls !== null
            ? [[t('gov.analytics.kpi.assistantCalls'), formatNumber(o.assistantCalls, lang)]]
            : []),
        ].map(([label, value, hint]) => (
          <Grid key={label} size={{ xs: 6, md: 3 }}>
            <Tile label={label} value={value} hint={hint} />
          </Grid>
        ))}
      </Grid>

      {o.financials && (
        <HairlineCard sx={{ p: 2 }}>
          <Typography variant="subtitle2">{t('gov.analytics.financials.title')}</Typography>
          <Typography variant="body2">
            {t('gov.analytics.financials.fines', {
              total: formatNumber(o.financials.finesTotal, lang, true),
              count: o.financials.finesCount,
            })}
          </Typography>
          <Typography variant="body2">
            {t('gov.analytics.financials.costs', {
              total: formatNumber(o.financials.costsTotal, lang, true),
              count: o.financials.costsCount,
            })}
          </Typography>
          {o.financials.unparsed > 0 && (
            <Typography variant="caption" sx={{ color: palette.text.secondary }}>
              {t('gov.analytics.financials.unparsed', { count: o.financials.unparsed })}
            </Typography>
          )}
        </HairlineCard>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Scrolls inside the card: at phone width the French column heads
              outgrow it, and the card clipped the last column instead. */}
          <HairlineCard sx={{ p: 2, overflowX: 'auto' }}>
            <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
              {t('gov.analytics.monthly.title')}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('gov.analytics.monthly.month')}</TableCell>
                  <TableCell align="right">{t('gov.analytics.monthly.opened')}</TableCell>
                  <TableCell align="right">{t('gov.analytics.monthly.closed')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {o.monthly.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell>
                      {frenchFirstOfMonth(
                        new Intl.DateTimeFormat(lang.startsWith('fr') ? 'fr-CA' : 'en-CA', {
                          month: 'long',
                          year: 'numeric',
                        }).format(new Date(+m.month.slice(0, 4), +m.month.slice(5, 7) - 1, 1)),
                      )}
                    </TableCell>
                    <TableCell align="right">{m.opened}</TableCell>
                    <TableCell align="right">{m.closed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </HairlineCard>
        </Grid>
        {o.timeInStage && (
          <Grid size={{ xs: 12, md: 6 }}>
            <HairlineCard sx={{ p: 2, overflowX: 'auto' }}>
              <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
                {t('gov.analytics.stages.title')}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('gov.analytics.stages.stage')}</TableCell>
                    <TableCell align="right">{t('gov.analytics.stages.median')}</TableCell>
                    <TableCell align="right">{t('gov.analytics.stages.average')}</TableCell>
                    <TableCell align="right">{t('gov.analytics.stages.transitions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {o.timeInStage.map((s) => (
                    <TableRow key={s.stage}>
                      <TableCell>{t(stageLabelKey(s.stage))}</TableCell>
                      <TableCell align="right">{formatNumber(s.medianDays, lang)}</TableCell>
                      <TableCell align="right">{formatNumber(s.averageDays, lang)}</TableCell>
                      <TableCell align="right">{s.transitions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </HairlineCard>
          </Grid>
        )}
      </Grid>
    </Stack>
  );
}

const METRICS: { key: keyof BenchmarkMetrics; rate: boolean }[] = [
  { key: 'filesOpened', rate: false },
  { key: 'filesClosed', rate: false },
  { key: 'medianDaysToClose', rate: false },
  { key: 'deadlineMetRate', rate: true },
  { key: 'complianceRate', rate: true },
];

function metricValue(
  m: BenchmarkMetrics,
  key: keyof BenchmarkMetrics,
  rate: boolean,
  lang: string,
) {
  return rate ? formatRate(m[key], lang) : formatNumber(m[key], lang);
}

function PeerView({ benchmark }: { benchmark: PeerBenchmark }) {
  const { t, i18n } = useTranslation();
  const metrics = METRICS.filter((m) => benchmark.tier === 'full' || m.key !== 'complianceRate');
  return (
    <HairlineCard sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" component="h3">
        {t('gov.analytics.peers.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: palette.text.secondary, mb: 1 }}>
        {benchmark.peers
          ? t('gov.analytics.peers.subtitle', { count: benchmark.peers.count })
          : t('gov.analytics.peers.tooFew', { count: benchmark.minPeers })}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('gov.analytics.peers.metric')}</TableCell>
            <TableCell align="right">{t('gov.analytics.peers.you')}</TableCell>
            {benchmark.peers && (
              <TableCell align="right">{t('gov.analytics.peers.median')}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {metrics.map(({ key, rate }) => (
            <TableRow key={key}>
              <TableCell>{t(`gov.analytics.metric.${key}`)}</TableCell>
              <TableCell align="right">
                {metricValue(benchmark.self, key, rate, i18n.language)}
              </TableCell>
              {benchmark.peers && (
                <TableCell align="right">
                  {metricValue(benchmark.peers.medians, key, rate, i18n.language)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </HairlineCard>
  );
}

function ContractorView({
  range,
  onPick,
}: {
  range: AnalyticsRange;
  onPick: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const benchmark = useContractorBenchmark(range, true);
  if (benchmark.isLoading) return <CircularProgress size={24} />;
  if (benchmark.isError || !benchmark.data) {
    return <Alert severity="error">{t('gov.analytics.loadError')}</Alert>;
  }
  const { rows, medians } = benchmark.data;
  if (!rows.length) return <Alert severity="info">{t('gov.analytics.contractor.empty')}</Alert>;
  return (
    <HairlineCard sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        {t('gov.analytics.contractor.title')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('gov.analytics.contractor.municipality')}</TableCell>
            {METRICS.map((m) => (
              <TableCell key={m.key} align="right">
                {t(`gov.analytics.metric.${m.key}`)}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.localGovernmentId}>
              <TableCell>{r.name}</TableCell>
              {METRICS.map((m) => (
                <TableCell key={m.key} align="right">
                  {metricValue(r.metrics, m.key, m.rate, i18n.language)}
                </TableCell>
              ))}
              <TableCell align="right">
                <Button size="small" onClick={() => onPick(r.localGovernmentId)}>
                  {t('gov.analytics.contractor.open')}
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {medians && (
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('gov.analytics.contractor.median')}</TableCell>
              {METRICS.map((m) => (
                <TableCell key={m.key} align="right" sx={{ fontWeight: 600 }}>
                  {metricValue(medians, m.key, m.rate, i18n.language)}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </HairlineCard>
  );
}

function MunicipalityDashboard({ id, range }: { id: string; range: AnalyticsRange }) {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  // The rail's own answer to "where is analytics on?". The overview and peer
  // queries used to find out with a 409 each, every time the page opened at a
  // municipality without the module.
  const availability = useAnalyticsAvailability(true);
  const off = availability.isSuccess && !availability.data.localGovernmentIds.includes(id);
  const askId = availability.isSuccess && !off ? id : undefined;
  const overview = useAnalyticsOverview(askId, range);
  const peers = usePeerBenchmark(askId, range);
  const [exporting, setExporting] = useState(false);

  if (off) {
    return <Alert severity="info">{t('gov.analytics.disabled')}</Alert>;
  }
  if (availability.isError) {
    return <Alert severity="error">{t('gov.analytics.loadError')}</Alert>;
  }
  if (availability.isLoading || overview.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (overview.isError) {
    return (
      <Alert severity={apiErrorStatus(overview.error) === 409 ? 'info' : 'error'}>
        {apiErrorStatus(overview.error) === 409
          ? t('gov.analytics.disabled')
          : apiErrorMessage(overview.error, t('gov.analytics.loadError'))}
      </Alert>
    );
  }
  if (!overview.data) return null;

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadAnalyticsCsv(id, range, i18n.language.startsWith('fr') ? 'fr' : 'en');
    } catch (err) {
      snackbar.error(apiErrorMessage(err, t('gov.analytics.exportFailed')));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="h2">
          {overview.data.localGovernmentName}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Icons.Download size={iconSize.md} aria-hidden />}
          disabled={exporting}
          onClick={() => void exportCsv()}
        >
          {t('gov.analytics.export')}
        </Button>
      </Stack>
      <OverviewView overview={overview.data} />
      {peers.data && <PeerView benchmark={peers.data} />}
    </Stack>
  );
}

/**
 * The council's enforcement dashboard.
 *
 * Working at a municipality, it shows that municipality — at full detail for
 * someone who reads its files, public counts otherwise — and how it compares
 * with the anonymised medians of its contractor's other municipalities.
 * Working for a contractor, it opens on the named comparison across every
 * municipality the contractor serves, and drills into any one of them.
 */
export function Analytics() {
  const { t } = useTranslation();
  const scope = useOptionalScope();
  const active = scope?.active ?? null;
  const [range, setRange] = useState<AnalyticsRange>(() => ({ from: daysAgo(89), to: daysAgo(0) }));
  const [picked, setPicked] = useState<string | null>(null);
  const contractor = !!scope?.can('analytics:benchmark');
  const lgOptions = useMemo(
    () => (scope?.scopes ?? []).filter((s) => s.kind === 'local_government'),
    [scope?.scopes],
  );

  const lgId = active?.kind === 'local_government' ? active.id : picked;

  return (
    <AppShell>
      <PageIntro>{t('gov.analytics.subtitle')}</PageIntro>
      <Stack spacing={2}>
        <RangePicker range={range} onChange={setRange} />
        {active?.kind === 'company' && (
          <Stack spacing={2}>
            {contractor && <ContractorView range={range} onPick={setPicked} />}
            {lgOptions.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value={picked ?? ''}
                onChange={(e) => setPicked((e.target.value as string) || null)}
                inputProps={{ 'aria-label': t('gov.analytics.pickMunicipality') }}
                sx={{ maxWidth: 360 }}
              >
                <MenuItem value="">{t('gov.analytics.pickMunicipality')}</MenuItem>
                {lgOptions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Stack>
        )}
        {!active && <Alert severity="info">{t('gov.analytics.pickMunicipality')}</Alert>}
        {lgId && <MunicipalityDashboard id={lgId} range={range} />}
      </Stack>
    </AppShell>
  );
}
